import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useThemeManagement } from './hooks/useThemeManagement';
import { usePlatform } from '../../context/PlatformContext';
import {
  ThemeManagementHeader,
  ThemeStatistics,
  ThemeTable,
  ThemeDetails,
  ThemePreviewPanel,
  ThemeCustomizer,
  ThemeFormDialog,
} from './components';

/**
 * ThemeManagement Component
 *
 * A comprehensive theme management system for customizing platform appearance,
 * branding, and styling for each tenant.
 *
 * Features:
 * - Create, edit, and delete themes
 * - Real-time theme statistics
 * - Color palette management
 * - Typography customization
 * - Custom CSS support
 * - Theme preview functionality
 * - Responsive design
 */
const ThemeManagement = () => {
  const {
    // State
    loading,
    error,
    themes,
    page,
    rowsPerPage,
    previewMode,
    openDialog,
    currentThemeItem,
    formData,

    // Actions
    setPreviewMode,
    fetchThemes,
    handleCloseDialog,
    handleSubmit,
    handleChange,
    handleColorChange,
    handleDelete,
    handleToggleTheme,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useThemeManagement();

  const { createTheme, updateTheme } = usePlatform();
  const [editingTheme, setEditingTheme] = useState(null);
  const [creatingNewTheme, setCreatingNewTheme] = useState(false);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Theme Management
      </Typography>

      {/* Header with Actions - Create button and refresh button hidden for admin website */}
      <ThemeManagementHeader
        loading={loading}
        onCreateTheme={() => {
          setCreatingNewTheme(true);
          setEditingTheme(null);
        }}
        onRefresh={fetchThemes}
        hideCreateButton={true}
        hideRefreshButton={true}
      />

      {/* Loading State */}
      {loading && <CircularProgress />}

      {/* Error State */}
      {error && <Alert severity="error">{error}</Alert>}

      {/* Theme Customizer or Standard View */}
      {editingTheme || creatingNewTheme ? (
        <ThemeCustomizer
          theme={editingTheme || null}
          onBack={() => {
            setEditingTheme(null);
            setCreatingNewTheme(false);
          }}
          onSave={async updatedTheme => {
            try {
              if (editingTheme) {
                // Update existing theme
                await updateTheme(editingTheme.theme_id, updatedTheme);
                setEditingTheme(null);
              } else {
                // Create new theme (tenant_id comes from auth context)
                await createTheme(updatedTheme);
                setCreatingNewTheme(false);
              }
              // Refresh themes list
              await fetchThemes();
            } catch (error) {
              throw error; // Re-throw to show error in ThemeCustomizer
            }
          }}
        />
      ) : (
        <>
          {/* Theme Statistics - Hidden for admin website */}
          {false && (
            <ThemeStatistics themes={themes} previewMode={previewMode} />
          )}

          {/* Themes Table */}
          <ThemeTable
            themes={themes}
            page={page}
            rowsPerPage={rowsPerPage}
            onEditTheme={theme => setEditingTheme(theme)}
            onDeleteTheme={handleDelete}
            onToggleTheme={handleToggleTheme}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />

          {/* Theme Details */}
          <ThemeDetails themes={themes} />
        </>
      )}

      {/* Theme Form Dialog for Create/Edit */}
      <ThemeFormDialog
        open={openDialog}
        currentThemeItem={currentThemeItem}
        formData={formData}
        loading={loading}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onChange={handleChange}
        onColorChange={handleColorChange}
      />

      {/* Theme Preview Panel */}
      <ThemePreviewPanel
        themes={themes}
        previewMode={previewMode}
        onClosePreview={() => setPreviewMode(false)}
      />
    </Box>
  );
};

export default ThemeManagement;
